import { z } from 'zod/v4';
import { Currency, TagSchema } from '../../index.js';
import { FileSchema } from '../../files/index.js';
import { PerformedBySchema } from '../../performed-by/index.js';
import { VoucherCategory, } from '../vouchers/index.js';
import { queryJSONSchema } from '../../util/validation/index.js';
import { booleanSchema } from '../../util/schema.js';
export var CaseVoucherSource;
(function (CaseVoucherSource) {
    CaseVoucherSource["Creditor"] = "CREDITOR";
    CaseVoucherSource["Collector"] = "COLLECTOR";
})(CaseVoucherSource || (CaseVoucherSource = {}));
export var State;
(function (State) {
    State["Unmapped"] = "UNMAPPED";
    State["Unknown"] = "UNKNOWN";
    State["Archived"] = "ARCHIVED";
    State["Overpayment"] = "OVERPAYMENT";
    State["FullPayment"] = "FULL_PAYMENT";
    State["PartialPayment"] = "PARTIAL_PAYMENT";
    State["PaymentPlanEntryPayment"] = "PAYMENT_PLAN_ENTRY_PAYMENT";
    State["UnkownPaymentPlanPayment"] = "UNKNOWN_PAYMENT_PLAN_PAYMENT";
    State["AccordPayment"] = "ACCORD_PAYMENT";
    State["PrincipalPayment"] = "PRINCIPAL_PAYMENT";
    State["Pending"] = "PENDING";
})(State || (State = {}));
export var CardType;
(function (CardType) {
    CardType["Mastercard"] = "MASTERCARD";
    CardType["Visa"] = "VISA";
    CardType["VisaElectron"] = "VISA_ELECTRON";
    CardType["VisaDankort"] = "VISA_DANKORT";
    CardType["Dankort"] = "DANKORT";
})(CardType || (CardType = {}));
export var PaymentType;
(function (PaymentType) {
    PaymentType["BankTransfer"] = "BANK_TRANSFER";
    PaymentType["Mastercard"] = "MASTERCARD";
    PaymentType["Visa"] = "VISA";
    PaymentType["Dankort"] = "DANKORT";
    PaymentType["IBAN"] = "IBAN";
    PaymentType["Creditor"] = "CREDITOR";
    PaymentType["PayPal"] = "PAYPAL";
    PaymentType["ApplePay"] = "APPLE_PAY";
    PaymentType["GooglePay"] = "GOOGLE_PAY";
    PaymentType["FIK"] = "FIK";
    PaymentType["Leverand\u00F8rservice"] = "LEVERAND\u00D8RSERVICE";
    PaymentType["Betalingsservice"] = "BETALINGSSERVICE";
    PaymentType["MobilePay"] = "MOBILEPAY";
    PaymentType["Swish"] = "SWISH";
    PaymentType["Vipps"] = "VIPPS";
})(PaymentType || (PaymentType = {}));
export var DeleteType;
(function (DeleteType) {
    DeleteType["WorkflowRollback"] = "WORKFLOW_ROLLBACK";
    DeleteType["CaseMerge"] = "CASE_MERGE";
})(DeleteType || (DeleteType = {}));
export const caseVoucherCategoryOrder = [
    VoucherCategory.PRINCIPAL,
    VoucherCategory.INTEREST,
    VoucherCategory.COST,
    VoucherCategory.COURTCOST,
    VoucherCategory.TRANSACTION,
];
export const CaseVoucherFilterSchema = z.object({
    getDeleted: booleanSchema.nullish(),
    missingCaseId: booleanSchema.nullish(),
    fromDate: z.iso.datetime({ offset: true }).optional(),
    toDate: z.iso.datetime({ offset: true }).optional(),
    fromExpirationDate: z.iso.datetime({ offset: true }).optional(),
    toExpirationDate: z.iso.datetime({ offset: true }).optional(),
    fromIrrecoverableDate: z.iso.datetime({ offset: true }).optional(),
    toIrrecoverableDate: z.iso.datetime({ offset: true }).optional(),
    fromAmount: z.coerce.number().int().optional(),
    toAmount: z.coerce.number().int().optional(),
    expired: z
        .string()
        .transform((val) => val === 'true')
        .optional(),
    exported: z
        .string()
        .transform((val) => val === 'true')
        .optional(),
    voucherTypeIds: queryJSONSchema(z.array(z.uuid()), 'voucherTypeIds need to be a list of uuids').optional(),
    currencies: queryJSONSchema(z.array(z.enum(Currency)), `currencies need to be a list of currencies (${Object.values(Currency)})`).optional(),
    sources: queryJSONSchema(z.array(z.enum(CaseVoucherSource)), `sources need to be a valid list of sources (${Object.values(CaseVoucherSource)})`).optional(),
    states: queryJSONSchema(z.array(z.enum(State)), `states need to be a valid list of states (${Object.values(State)})`).optional(),
    sortKey: z
        .enum([
        'id',
        'date',
        'text',
        'source',
        'amount',
        'currency',
        'expirationDate',
        'irrecoverableDate',
        'createdAt',
        'paymentType',
        'expired',
        'exported',
    ])
        .optional(),
    creditorId: z.uuid().nullish(),
    sortDirection: z.enum(['ASC', 'DESC']).optional(),
});
export const LineItemSchema = z.object({
    id: z.string(),
    name: z.string(),
    quantity: z.number(),
    price: z.number().int(),
    taxRate: z.number(),
    taxAmount: z.number(),
    netAmount: z.number().int(),
});
const BaseCreateCaseVoucherSchema = z.object({
    source: z.enum(CaseVoucherSource),
    currency: z.enum(Currency).optional(),
    amount: z.number(),
    date: z.string(),
    voucherTypeId: z.uuid(),
    caseId: z.uuid().nullish(),
    caseVoucherId: z.uuid().nullish(),
    dueDate: z.string().nullish(),
    interest: z.number().nullish(),
    interestStartDate: z.string().nullish(),
    interestEndDate: z.string().nullish(),
    appendix: FileSchema.nullish(),
    createdAt: z.string().nullish(),
    text: z.string().nullish(),
    tags: z.array(TagSchema).optional(),
    referenceId: z.string().nullish(),
    importId: z.uuid().nullish(),
    referenceCaseVoucherId: z.uuid().nullish(),
    createdBy: PerformedBySchema.nullish(),
    details: z.any().optional(),
    lineItems: z.array(LineItemSchema).nullish(),
    transactionAccountId: z.uuid().nullish(),
    state: z.enum(State).nullish(),
    expirationFromDate: z.string().nullish(),
    properties: z.any().nullish(),
    uniqueId: z.string().nullish(),
    paymentType: z.enum(PaymentType).nullish(),
});
export const CreateCaseVoucherSchema = BaseCreateCaseVoucherSchema.extend({
    referenceCaseVouchers: z.lazy(() => z.array(CreateCaseVoucherSchema).optional()),
});
export const PatchCaseVoucherSchema = z.object({
    caseId: z.uuid().optional(),
    referenceCaseVoucherId: z.uuid().nullish(),
    expired: z.boolean().optional(),
    state: z.enum(State).nullish(),
    irrecoverableDate: z.string().nullish(),
    referenceId: z.string().optional(),
    text: z.string().nullish(),
    date: z.string().optional(),
    dueDate: z.string().optional(),
    interestStartDate: z.string().optional(),
    interestEndDate: z.string().nullish(),
    amount: z.number().optional(),
    interest: z.number().min(0).nullish(),
    appendix: FileSchema.nullish(),
    expirationDate: z.string().nullish(),
    expirationFromDate: z.string().nullish(),
    expirationManualSet: z.boolean().optional(),
});
//# sourceMappingURL=index.js.map